// Admin → Payments
// Lists payment settings for the current facility and lets the admin:
//   - Switch default processor (Stripe / Square)
//   - Onboard with Stripe Connect (Express) for destination charges
//   - View Square placeholder fields for future
//   - See live charges_enabled / payouts_enabled status from Stripe

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.jsx';

export default function AdminPayments() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const justReturned = params.get('stripe') === 'onboard_return';

  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(justReturned ? 'Welcome back from Stripe onboarding. Status refreshed below.' : null);

  async function load() {
    try { setData(await api.paymentSettings()); setErr(null); }
    catch (e) { setErr(e.message); }
  }
  useEffect(() => { load(); }, []);

  if (user?.role !== 'admin') {
    return <div className="card p-6 text-red-700 font-medium">Admin role required</div>;
  }

  async function setDefault(p) {
    setBusy(true);
    try { await api.updatePaymentSettings({ default_processor: p }); load(); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function startOnboard() {
    setBusy(true); setErr(null);
    try {
      const r = await api.stripeOnboard();
      window.location.href = r.url; // hosted Stripe onboarding form
    } catch (e) { setErr(e.body?.message || e.message); setBusy(false); }
  }

  if (!data) {
    if (err) {
      return (
        <div className="space-y-3 max-w-3xl">
          <h1 className="text-2xl font-bold">Payments</h1>
          <div className="card p-3 text-sm text-red-700 bg-red-50 border-red-200">
            Could not load payment settings: {err}
          </div>
          <button className="btn-ghost" onClick={() => { setErr(null); load(); }}>Retry</button>
        </div>
      );
    }
    return <div className="text-slate-500">Loading payment settings…</div>;
  }

  const s = data.payment_settings;
  const cs = data.connect_status;
  const stripeOk = !!data.stripe_configured;
  const ready = !!s.stripe_charges_enabled;

  return (
    <div className="space-y-4 max-w-3xl">
      <div>
        <h1 className="text-2xl font-bold">Payments</h1>
        <p className="text-sm text-slate-500">
          Configure how payments flow for this facility. Funds settle to your facility's connected account, not the M2B platform.
        </p>
      </div>

      {msg && <div className="card p-3 text-sm text-emerald-700 bg-emerald-50 border-emerald-200">{msg}</div>}
      {err && <div className="card p-3 text-sm text-red-700 bg-red-50 border-red-200">{err}</div>}

      <div className="card p-4 space-y-3">
        <div className="font-medium">Default processor</div>
        <div className="flex gap-2">
          <button className={`btn ${s.default_processor === 'stripe' ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => setDefault('stripe')} disabled={busy}>Stripe</button>
          <button className={`btn ${s.default_processor === 'square' ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => setDefault('square')} disabled={busy}>Square</button>
        </div>
        <div className="text-xs text-slate-500">
          The Stripe adapter is fully implemented; Square is wired as a stub and will return "not implemented" if selected for live payments.
        </div>
      </div>

      <div className="card p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="font-medium">Stripe Connect (destination charges)</div>
          <span className={`text-xs px-2 py-0.5 rounded ${ready ? 'bg-emerald-100 text-emerald-800'
            : s.stripe_connect_account_id ? 'bg-amber-100 text-amber-800' : 'bg-slate-200 text-slate-700'}`}>
            {ready ? 'Active' : s.stripe_connect_account_id ? 'Onboarding incomplete' : 'Not connected'}
          </span>
        </div>
        {!stripeOk && (
          <div className="text-sm text-amber-700 bg-amber-50 rounded p-3">
            STRIPE_SECRET_KEY is not set on the API. Add it on Render → m2b-api → Environment, then redeploy.
          </div>
        )}
        {s.stripe_connect_account_id && (
          <div className="text-xs text-slate-600 font-mono">Account: {s.stripe_connect_account_id}</div>
        )}
        {cs && (
          <ul className="text-sm text-slate-700 space-y-0.5">
            <li>Charges enabled: <strong className={cs.charges_enabled ? 'text-emerald-700' : 'text-amber-700'}>{String(cs.charges_enabled)}</strong></li>
            <li>Payouts enabled: <strong className={cs.payouts_enabled ? 'text-emerald-700' : 'text-amber-700'}>{String(cs.payouts_enabled)}</strong></li>
            <li>Details submitted: <strong>{String(cs.details_submitted)}</strong></li>
            {cs.requirements_currently_due?.length > 0 && (
              <li className="text-amber-800">Outstanding: {cs.requirements_currently_due.join(', ')}</li>
            )}
          </ul>
        )}
        <div className="flex gap-2 flex-wrap">
          <button className="btn-primary" disabled={!stripeOk || busy} onClick={startOnboard}>
            {s.stripe_connect_account_id ? 'Continue Stripe onboarding' : 'Connect with Stripe'}
          </button>
          <button className="btn-ghost" onClick={load} disabled={busy}>Refresh status</button>
        </div>
        <div className="text-xs text-slate-500">
          You'll be redirected to Stripe to complete identity, business, and bank account setup. Stripe handles all of it. Returning to this page refreshes status automatically.
        </div>
      </div>

      <div className="card p-4 space-y-3 opacity-90">
        <div className="font-medium">Square (placeholder)</div>
        <div className="text-xs text-slate-500">
          Square Web Payments + Reader integration is stubbed. Saving these fields keeps them ready for the next phase.
        </div>
        <SquareFields current={s} disabled={busy} onSave={async (body) => {
          setBusy(true);
          try { await api.updatePaymentSettings(body); load(); setMsg('Saved.'); }
          catch (e) { setErr(e.message); }
          finally { setBusy(false); }
        }} />
      </div>
    </div>
  );
}

function SquareFields({ current, disabled, onSave }) {
  const [loc, setLoc] = useState(current.square_location_id || '');
  const [appId, setAppId] = useState(current.square_application_id || '');
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <label className="block text-sm">
        <span className="text-xs text-slate-600">Square Location ID</span>
        <input className="input font-mono" value={loc} onChange={e => setLoc(e.target.value)} placeholder="L7AKVH..." />
      </label>
      <label className="block text-sm">
        <span className="text-xs text-slate-600">Square Application ID</span>
        <input className="input font-mono" value={appId} onChange={e => setAppId(e.target.value)} placeholder="sandbox-sq0idb-..." />
      </label>
      <div className="sm:col-span-2">
        <button className="btn-ghost" disabled={disabled}
          onClick={() => onSave({ square_location_id: loc || null, square_application_id: appId || null })}>
          Save Square config
        </button>
      </div>
    </div>
  );
}
